import api from './api'

/**
 * API do MDM (tablets/celulares Android do tipo "mobile").
 * Contrato: Automais.MDM/docs/CONTRACT.md §7.
 */
export const mdmApi = {
  getFleet: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/fleet`)
    return response.data
  },

  getEnrollQr: async (tenantId, enrollLinkId) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/enroll-qr`, { params: { enrollLinkId } })
    return response.data
  },

  sendBulkCommand: async (tenantId, { deviceIds, type, payload }) => {
    const response = await api.post(`/tenants/${tenantId}/mdm/commands/bulk`, { deviceIds, type, payload: payload ?? null })
    return response.data
  },

  getState: async (deviceId) => {
    const response = await api.get(`/managed-devices/${deviceId}/mdm`)
    return response.data
  },

  updatePolicy: async (deviceId, policy) => {
    const response = await api.put(`/managed-devices/${deviceId}/mdm/policy`, policy)
    return response.data
  },

  getCommands: async (deviceId, limit = 50) => {
    const response = await api.get(`/managed-devices/${deviceId}/mdm/commands`, { params: { limit } })
    return response.data
  },

  sendCommand: async (deviceId, type, payload = null) => {
    const response = await api.post(`/managed-devices/${deviceId}/mdm/commands`, { type, payload })
    return response.data
  },

  /** Wipe tem rota própria (MFA step-up; o interceptor de api.js trata o 428). */
  wipe: async (deviceId, reason) => {
    const response = await api.post(`/managed-devices/${deviceId}/mdm/wipe`, { reason })
    return response.data
  },

  cancelCommand: async (deviceId, commandId) => {
    const response = await api.post(`/managed-devices/${deviceId}/mdm/commands/${commandId}/cancel`)
    return response.data
  },

  getLocations: async (deviceId, { from, to }) => {
    const response = await api.get(`/managed-devices/${deviceId}/mdm/locations`, { params: { from, to } })
    return response.data
  },

  getApps: async (deviceId) => {
    const response = await api.get(`/managed-devices/${deviceId}/mdm/apps`)
    return response.data
  },

  uploadFile: async (deviceId, file, onProgress) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post(`/managed-devices/${deviceId}/mdm/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded * 100) / e.total)) }
        : undefined,
    })
    return response.data
  },

  // ── Catálogo de apps gerenciados do tenant (CONTRACT.md §7.1) ──

  listCatalogApps: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/apps`)
    return response.data
  },

  /** Sobe um APK para o catálogo; o servidor lê pacote/versão do próprio APK. APK pode ter ~160 MB. */
  uploadCatalogApp: async (tenantId, file, label, onProgress) => {
    const form = new FormData()
    form.append('file', file)
    if (label && label.trim()) form.append('label', label.trim())
    const response = await api.post(`/tenants/${tenantId}/mdm/apps`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded * 100) / e.total)) }
        : undefined,
    })
    return response.data
  },

  deleteCatalogApp: async (tenantId, appId) => {
    const response = await api.delete(`/tenants/${tenantId}/mdm/apps/${appId}`)
    return response.data
  },

  /**
   * Cria um install-apk por device; com kiosk=true também grava o app como quiosque na política.
   * allInScope=true instala em todos os tablets do grupo groupId (com subgrupos; null = tenant) e o
   * quiosque vai para a política do grupo.
   */
  installCatalogApp: async (tenantId, appId, { deviceIds = [], kiosk = false, allInScope = false, groupId = null }) => {
    const response = await api.post(`/tenants/${tenantId}/mdm/apps/${appId}/install`, { deviceIds, kiosk, allInScope, groupId })
    return response.data
  },

  // ── Grupos: política herdada e ações no escopo (CONTRACT.md §11) ──

  getScopePolicy: async (tenantId, groupId = null) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/scope-policy`, { params: groupId ? { groupId } : {} })
    return response.data
  },

  /** policy null = o escopo volta a herdar (na raiz: política padrão). */
  setScopePolicy: async (tenantId, { groupId = null, policy = null }) => {
    const response = await api.put(`/tenants/${tenantId}/mdm/scope-policy`, { groupId, policy })
    return response.data
  },

  /** Comando para todos os tablets do grupo + subgrupos (groupId null = tenant), resolvidos no servidor. */
  sendScopeCommand: async (tenantId, { groupId = null, type, payload = null }) => {
    const response = await api.post(`/tenants/${tenantId}/mdm/scope-commands`, { groupId, type, payload })
    return response.data
  },

  /** O tablet descarta a política própria e volta a seguir a do grupo. */
  inheritPolicy: async (deviceId) => {
    const response = await api.post(`/managed-devices/${deviceId}/mdm/policy/inherit`)
    return response.data
  },

  // ── Versão do agente MDM (CONTRACT.md §9) ──

  listAgentVersions: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/agent-versions`)
    return response.data
  },

  listAgentTargets: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/agent-targets`)
    return response.data
  },

  /** Versão-alvo de um grupo (vale para os subgrupos) ou da raiz (groupId null). version null = herda. */
  setScopeAgentTarget: async (tenantId, { groupId = null, version = null }) => {
    const response = await api.put(`/tenants/${tenantId}/mdm/agent-target`, { groupId, version })
    return response.data
  },

  /** Fixa a versão neste tablet; version null = volta a herdar do grupo. */
  setDeviceAgentTarget: async (deviceId, version = null) => {
    const response = await api.put(`/managed-devices/${deviceId}/mdm/agent-target`, { version })
    return response.data
  },

  /** Baixa um arquivo do device (screenshot, pull-file) como Blob — precisa do Bearer. */
  downloadFile: async (deviceId, fileId) => {
    const response = await api.get(`/managed-devices/${deviceId}/mdm/files/${fileId}`, { responseType: 'blob' })
    return response.data
  },

  // ── Agente 1.1: acesso local, eventos e marca (CONTRACT.md §7, §12) ──

  /**
   * Código de uso único para o técnico no tablet (§12.3). purpose "admin" | "remove".
   * "remove" exige MFA step-up (managed-devices.mdm-remove-code) — o interceptor de api.js trata o 428,
   * igual ao wipe.
   */
  issueLocalCode: async (deviceId, { purpose, reason }) => {
    const response = await api.post(`/managed-devices/${deviceId}/mdm/local-codes`, { purpose, reason })
    return response.data
  },

  /** Eventos locais do tablet + códigos gerados no painel (mais novo primeiro). */
  getEvents: async (deviceId, limit = 100) => {
    const response = await api.get(`/managed-devices/${deviceId}/mdm/events`, { params: { limit } })
    return response.data
  },

  /** Logo do cliente (PNG/JPEG/WebP ≤ 512 KB) → { fileId, sha256, contentType, size }. */
  uploadBrandingLogo: async (tenantId, file) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post(`/tenants/${tenantId}/mdm/branding-logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  /** Logo do cliente como Blob (prévia no formulário) — precisa do Bearer. */
  downloadBrandingLogo: async (tenantId, fileId) => {
    const response = await api.get(`/tenants/${tenantId}/mdm/branding-logo/${fileId}`, { responseType: 'blob' })
    return response.data
  },
}

/** Status de comando que não muda mais. */
export const FINAL_COMMAND_STATUSES = ['Done', 'Failed', 'Expired', 'Canceled']

export const isFinalCommandStatus = (status) => FINAL_COMMAND_STATUSES.includes(status)

/** Kinds do tipo mobile (CONTRACT.md cabeçalho). */
export const MOBILE_KINDS = ['AndroidTablet', 'AndroidPhone']

export const isMobileDevice = (device) =>
  !!device && (MOBILE_KINDS.includes(device.kind) || device.kindCategory === 'Mobile')

/** Política default (CONTRACT.md §5) — usada para preencher campos ausentes. */
export const DEFAULT_MDM_POLICY = {
  restrictions: {
    disableCamera: false,
    disableScreenCapture: false,
    disableStatusBar: false,
    disableUsbFileTransfer: true,
    disableFactoryReset: true,
    disableSafeBoot: true,
    disableAddUser: true,
    disableInstallUnknownSources: true,
    disableUninstallApps: true,
    disableConfigWifi: false,
    disableBluetooth: false,
    disableAdjustVolume: false,
    disableLocationOff: true,
  },
  password: { quality: 'none', minLength: 4 },
  kiosk: { enabled: false, packages: [], showHome: false, showNotifications: false, showSystemInfo: true },
  apps: { blocked: [], hidden: [] },
  // Sem `enabled` de propósito: ausente = legado (§12.6).
  vpn: { alwaysOn: true, lockdown: true },
  location: { enabled: true, intervalSeconds: 60 },
  keepScreenOnWhileCharging: false,
  kioskUi: {
    statusBar: true,
    quickPanel: { wifi: true, rotation: true, brightness: true, volume: true, support: true },
  },
  // Sem `pinHash` de propósito: o front só repassa o que veio do servidor (§5).
  localAdmin: {
    gestureTaps: 5,
    volumeKeys: true,
    maxExitMinutes: 120,
    idleRelockSeconds: 180,
    allowPause: true,
    maxPauseMinutes: 480,
    allowRemove: false,
    remoteConsent: 'notify',
    requireAccessibility: true,
  },
  branding: { logoFileId: null, logoSha256: null, orgName: null, supportPhone: null, supportHours: null },
}

/**
 * Mescla a política recebida com o default (campos ausentes = default). Campos que o formulário
 * não conhece (top-level ou dentro das seções) são preservados para voltar intactos no salvar.
 */
export function normalizePolicy(policy) {
  const p = policy || {}
  const d = DEFAULT_MDM_POLICY
  const kioskUi = p.kioskUi || {}
  return {
    ...p,
    restrictions: { ...d.restrictions, ...(p.restrictions || {}) },
    password: { ...d.password, ...(p.password || {}) },
    kiosk: { ...d.kiosk, ...(p.kiosk || {}) },
    apps: { ...d.apps, ...(p.apps || {}) },
    vpn: { ...d.vpn, ...(p.vpn || {}) },
    location: { ...d.location, ...(p.location || {}) },
    keepScreenOnWhileCharging: p.keepScreenOnWhileCharging ?? d.keepScreenOnWhileCharging,
    kioskUi: {
      ...d.kioskUi,
      ...kioskUi,
      quickPanel: { ...d.kioskUi.quickPanel, ...(kioskUi.quickPanel || {}) },
    },
    localAdmin: { ...d.localAdmin, ...(p.localAdmin || {}) },
    branding: { ...d.branding, ...(p.branding || {}) },
  }
}
